import { areaSuffix } from '../../utils/serviceUnits';
import styled from 'styled-components';
import tw from 'twin.macro';
import { ServiceType } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  ErrorText,
  Field,
  FormGrid,
  Input,
  Muted,
  Stack,
  Strong,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../../components/ui';
import {
  CLEANING_LEVELS,
  CLEANING_LEVEL_LABELS,
  estimatedMinutes,
  resetToDefaults,
  SERVICE_TYPES,
  SERVICE_TYPE_DESCRIPTIONS,
  SERVICE_TYPE_LABELS,
  VEHICLE_CLASSES,
  VEHICLE_CLASS_LABELS,
  wageNote,
  warningField,
  warningsByField,
  warningText,
  type HomePriceForm,
  type OfferingForm,
  type PriceErrors,
  type PriceWarning,
} from './priceTables';
import { MoneyInput, NumberInput, ToggleRow, WageLine } from './serviceControls';

/**
 * Every price of a worker (spec 019 W6 step 2, W7a): home turnover by size,
 * the vehicle 4 × 3 table and the handover hourly rate, plus add-ons. Each
 * price shows what it works out to per hour and warns below the local minimum
 * wage (D26). Warnings never block a save.
 */

export interface OfferingsEditorProps {
  forms: Record<ServiceType, OfferingForm>;
  errors: Partial<Record<ServiceType, PriceErrors>>;
  onChange: (serviceType: ServiceType, form: OfferingForm) => void;
  /** The location's minimum wage per hour; null while unknown. */
  minimumWage: number | null;
  /** The API's warnings from the last save, used when the wage is unknown here. */
  serverWarnings: readonly PriceWarning[];
  /** D15: a handover needs this worker's verified background check. */
  handoverAllowed: boolean;
}

const CellInput = styled.div`
  min-width: 96px;
`;

const OverrideRow = styled.div`
  ${tw`grid items-start gap-2`}
  grid-template-columns: 1fr 1fr 1fr auto;
`;

function PriceNote({
  form,
  priceKey,
  price,
  minimumWage,
  warnings,
}: {
  form: OfferingForm;
  priceKey: string;
  price: string;
  minimumWage: number | null;
  warnings: Map<string, PriceWarning>;
}) {
  const minutes = estimatedMinutes(form.serviceType, priceKey, form.addOns);
  if (minimumWage !== null) {
    const note = wageNote(price, minutes, minimumWage);
    if (!note) return null;
    return (
      <WageLine $below={note.below} data-testid={note.below ? 'wage-warning' : undefined}>
        {note.text}
      </WageLine>
    );
  }
  const warning = warnings.get(warningField(form.serviceType, priceKey));
  return warning ? (
    <WageLine $below data-testid="wage-warning">
      {warningText(warning)}
    </WageLine>
  ) : null;
}

function HomeEditor({
  form,
  errors,
  onChange,
  note,
}: {
  form: OfferingForm;
  errors: PriceErrors;
  onChange: (home: HomePriceForm) => void;
  note: (key: string, price: string) => JSX.Element | null;
}) {
  const home = form.home;
  const set = (patch: Partial<HomePriceForm>) => onChange({ ...home, ...patch });
  const setOverride = (index: number, patch: Partial<HomePriceForm['overrides'][number]>) =>
    set({ overrides: home.overrides.map((o, i) => (i === index ? { ...o, ...patch } : o)) });
  return (
    <Stack $gap={4}>
      <FormGrid $cols={3}>
        <Field
          label="Base price"
          htmlFor="home-base"
          hint="1 bedroom, 1 bathroom"
          error={errors['home.basePrice']}
        >
          <MoneyInput
            id="home-base"
            value={home.basePrice}
            invalid={!!errors['home.basePrice']}
            onChange={basePrice => set({ basePrice })}
            testId="home-base-price"
          />
          {note('basePrice', home.basePrice)}
        </Field>
        <Field
          label="Each extra bedroom"
          htmlFor="home-bedroom"
          error={errors['home.perExtraBedroom']}
        >
          <MoneyInput
            id="home-bedroom"
            value={home.perExtraBedroom}
            invalid={!!errors['home.perExtraBedroom']}
            onChange={perExtraBedroom => set({ perExtraBedroom })}
          />
          {note('perExtraBedroom', home.perExtraBedroom)}
        </Field>
        <Field
          label="Each extra bathroom"
          htmlFor="home-bathroom"
          error={errors['home.perExtraBathroom']}
        >
          <MoneyInput
            id="home-bathroom"
            value={home.perExtraBathroom}
            invalid={!!errors['home.perExtraBathroom']}
            onChange={perExtraBathroom => set({ perExtraBathroom })}
          />
          {note('perExtraBathroom', home.perExtraBathroom)}
        </Field>
      </FormGrid>
      <div>
        <Strong>Set prices for some home sizes</Strong>
        <div>
          <Muted $size="sm">
            Optional. These replace the price worked out from bedrooms and bathrooms.
          </Muted>
        </div>
      </div>
      {home.overrides.map((o, index) => (
        <div key={index}>
          <OverrideRow>
            <Field
              label={index === 0 ? 'Bedrooms' : undefined}
              error={errors[`home.overrides.${index}.bedrooms`]}
            >
              <NumberInput
                id={`home-override-${index}-bedrooms`}
                value={o.bedrooms}
                invalid={!!errors[`home.overrides.${index}.bedrooms`]}
                onChange={bedrooms => setOverride(index, { bedrooms })}
              />
            </Field>
            <Field
              label={index === 0 ? 'Bathrooms' : undefined}
              error={errors[`home.overrides.${index}.bathrooms`]}
            >
              <Input
                id={`home-override-${index}-bathrooms`}
                inputMode="decimal"
                value={o.bathrooms}
                aria-invalid={!!errors[`home.overrides.${index}.bathrooms`] || undefined}
                onChange={event => setOverride(index, { bathrooms: event.target.value })}
              />
            </Field>
            <Field
              label={index === 0 ? 'Price' : undefined}
              error={errors[`home.overrides.${index}.price`]}
            >
              <MoneyInput
                id={`home-override-${index}-price`}
                value={o.price}
                invalid={!!errors[`home.overrides.${index}.price`]}
                onChange={price => setOverride(index, { price })}
              />
            </Field>
            <div style={{ paddingTop: index === 0 ? 26 : 0 }}>
              <Button
                type="button"
                theme="text"
                size="sm"
                text="Remove"
                onClick={() => set({ overrides: home.overrides.filter((_, i) => i !== index) })}
              />
            </div>
          </OverrideRow>
          {note(`overrides.${o.bedrooms}bd${o.bathrooms}ba`, o.price)}
        </div>
      ))}
      <div>
        <Button
          type="button"
          theme="secondary"
          size="sm"
          text="Add a home size"
          onClick={() =>
            set({ overrides: [...home.overrides, { bedrooms: '', bathrooms: '', price: '' }] })
          }
          dataTestId="home-add-override"
        />
      </div>
      <FormGrid $cols={3}>
        <Field
          label="Large home surcharge"
          htmlFor="home-large"
          hint="Optional"
          error={errors['home.largeHomeSurcharge']}
        >
          <MoneyInput
            id="home-large"
            value={home.largeHomeSurcharge}
            invalid={!!errors['home.largeHomeSurcharge']}
            onChange={largeHomeSurcharge => set({ largeHomeSurcharge })}
          />
        </Field>
        <Field
          label="Large home from"
          htmlFor="home-large-sqft"
          error={errors['home.largeHomeSqft']}
        >
          <NumberInput
            id="home-large-sqft"
            value={home.largeHomeSqft}
            trailing={areaSuffix(home.largeHomeAreaUnit ?? 'sqft')}
            invalid={!!errors['home.largeHomeSqft']}
            onChange={largeHomeSqft => set({ largeHomeSqft })}
          />
        </Field>
        <Field
          label="Same-day turnover surcharge"
          htmlFor="home-same-day"
          hint="Optional. Guests leave and arrive the same day."
          error={errors['home.sameDayTurnoverSurcharge']}
        >
          <MoneyInput
            id="home-same-day"
            value={home.sameDayTurnoverSurcharge}
            invalid={!!errors['home.sameDayTurnoverSurcharge']}
            onChange={sameDayTurnoverSurcharge => set({ sameDayTurnoverSurcharge })}
          />
        </Field>
      </FormGrid>
    </Stack>
  );
}

function VehicleEditor({
  form,
  errors,
  onChange,
  note,
}: {
  form: OfferingForm;
  errors: PriceErrors;
  onChange: (vehicle: OfferingForm['vehicle']) => void;
  note: (key: string, price: string) => JSX.Element | null;
}) {
  return (
    <Stack $gap={4}>
      <Muted $size="sm">Leave a box empty to not offer that size and level.</Muted>
      <TableWrap>
        <Table data-testid="vehicle-price-table">
          <THead>
            <TR>
              <TH>Vehicle</TH>
              {CLEANING_LEVELS.map(level => (
                <TH key={level}>{CLEANING_LEVEL_LABELS[level]}</TH>
              ))}
            </TR>
          </THead>
          <TBody>
            {VEHICLE_CLASSES.map(vehicleClass => (
              <TR key={vehicleClass}>
                <TD>{VEHICLE_CLASS_LABELS[vehicleClass]}</TD>
                {CLEANING_LEVELS.map(level => {
                  const field = `vehicle.${vehicleClass}.${level}`;
                  const value = form.vehicle[vehicleClass][level];
                  return (
                    <TD key={level}>
                      <CellInput>
                        <MoneyInput
                          id={`vehicle-${vehicleClass}-${level}`}
                          value={value}
                          placeholder="Not offered"
                          invalid={!!errors[field]}
                          onChange={next =>
                            onChange({
                              ...form.vehicle,
                              [vehicleClass]: { ...form.vehicle[vehicleClass], [level]: next },
                            })
                          }
                          testId={`vehicle-${vehicleClass}-${level}`}
                        />
                        {errors[field] && <ErrorText>{errors[field]}</ErrorText>}
                        {note(`${vehicleClass}.${level}`, value)}
                      </CellInput>
                    </TD>
                  );
                })}
              </TR>
            ))}
          </TBody>
        </Table>
      </TableWrap>
      {errors.vehicle && <ErrorText role="alert">{errors.vehicle}</ErrorText>}
    </Stack>
  );
}

function AddOnsEditor({
  form,
  errors,
  onChange,
  note,
}: {
  form: OfferingForm;
  errors: PriceErrors;
  onChange: (addOns: OfferingForm['addOns']) => void;
  note: (key: string, price: string) => JSX.Element | null;
}) {
  if (!form.addOns.length) return null;
  return (
    <Stack $gap={4}>
      <Strong>Add-ons</Strong>
      <FormGrid $cols={3}>
        {form.addOns.map((addOn, index) => (
          <Field
            key={addOn.code}
            label={addOn.label}
            htmlFor={`addon-${form.serviceType}-${addOn.code}`}
            hint={addOn.unit === 'per_item' ? 'Per item' : 'Per job'}
            error={errors[`addOns.${index}`]}
          >
            <MoneyInput
              id={`addon-${form.serviceType}-${addOn.code}`}
              value={addOn.price}
              invalid={!!errors[`addOns.${index}`]}
              onChange={price =>
                onChange(form.addOns.map((a, i) => (i === index ? { ...a, price } : a)))
              }
            />
            {note(`addOns.${addOn.code}`, addOn.price)}
          </Field>
        ))}
      </FormGrid>
    </Stack>
  );
}

export function ServiceOfferingCard({
  form,
  errors = {},
  onChange,
  minimumWage,
  serverWarnings,
  handoverAllowed,
}: {
  form: OfferingForm;
  errors?: PriceErrors;
  onChange: (form: OfferingForm) => void;
  minimumWage: number | null;
  serverWarnings: readonly PriceWarning[];
  handoverAllowed: boolean;
}) {
  const warnings = warningsByField(serverWarnings);
  const note = (priceKey: string, price: string) => (
    <PriceNote
      form={form}
      priceKey={priceKey}
      price={price}
      minimumWage={minimumWage}
      warnings={warnings}
    />
  );
  const isHandover = form.serviceType === ServiceType.VehicleHandover;
  const locked = isHandover && !handoverAllowed && !form.enabled;
  return (
    <Card data-testid={`offering-${form.serviceType}`}>
      <CardHeader
        title={SERVICE_TYPE_LABELS[form.serviceType]}
        description={SERVICE_TYPE_DESCRIPTIONS[form.serviceType]}
        actions={
          form.enabled && !isHandover ? (
            <Button
              type="button"
              theme="text"
              size="sm"
              text="Use suggested prices"
              onClick={() => onChange(resetToDefaults(form))}
            />
          ) : undefined
        }
      />
      <CardBody>
        <Stack $gap={4}>
          <ToggleRow
            label="Offer this service"
            checked={form.enabled}
            disabled={locked}
            onChange={enabled => onChange({ ...form, enabled })}
            testId={`offering-${form.serviceType}-toggle`}
          />
          {locked && (
            <Alert tone="info" title="Background check needed">
              Upload this worker's background check in the credentials step. You can offer handovers
              once we verify it.
            </Alert>
          )}
          {form.enabled && form.serviceType === ServiceType.HomeTurnover && (
            <HomeEditor
              form={form}
              errors={errors}
              onChange={home => onChange({ ...form, home })}
              note={note}
            />
          )}
          {form.enabled && form.serviceType === ServiceType.VehicleCleaning && (
            <VehicleEditor
              form={form}
              errors={errors}
              onChange={vehicle => onChange({ ...form, vehicle })}
              note={note}
            />
          )}
          {form.enabled && isHandover && (
            <FormGrid>
              <Field
                label="Hourly rate"
                htmlFor="handover-rate"
                required
                error={errors.hourlyRate}
                hint="Billed for the time the handover takes, at least 1 hour."
              >
                <MoneyInput
                  id="handover-rate"
                  value={form.hourlyRate}
                  trailing="/hr"
                  invalid={!!errors.hourlyRate}
                  onChange={hourlyRate => onChange({ ...form, hourlyRate })}
                  testId="handover-rate"
                />
                {note('hourlyRate', form.hourlyRate)}
              </Field>
            </FormGrid>
          )}
          {form.enabled && (
            <AddOnsEditor
              form={form}
              errors={errors}
              onChange={addOns => onChange({ ...form, addOns })}
              note={note}
            />
          )}
        </Stack>
      </CardBody>
    </Card>
  );
}

export function OfferingsEditor({
  forms,
  errors,
  onChange,
  minimumWage,
  serverWarnings,
  handoverAllowed,
}: OfferingsEditorProps) {
  return (
    <Stack $gap={6}>
      {SERVICE_TYPES.map(type => (
        <ServiceOfferingCard
          key={type}
          form={forms[type]}
          errors={errors[type]}
          onChange={next => onChange(type, next)}
          minimumWage={minimumWage}
          serverWarnings={serverWarnings}
          handoverAllowed={handoverAllowed}
        />
      ))}
    </Stack>
  );
}
